// The CCA-decides shell: a door, then a landing — and the design previews
// behind both.
//
// ── AND TWO SCREENS BEHIND IT ARE NO LONGER PREVIEWS ───────────────────────
// `#register/<electionId>` renders the ATTENDANCE REGISTER against real data
// through `GET|POST /api/register/:electionId` — the first surface in this app
// that read a real sitting, and the first that wrote anything at all. It is
// Admin only, decided server-side; a Scrutineer reaching the same fragment is
// shown a refusal.
//
// `#ballot/<ballotId>` renders the CAST SCREEN — a member of Synod's own ballot
// paper, issued and recorded through `POST /api/ballot/:ballotId/paper` and
// `POST /api/ballot/:ballotId/marks`. It is the second surface that writes, and
// the only one a member ever sees write. Standing to hold a paper comes from the
// frozen ROLL rather than the grant register (registrar ruling D2), which is why
// an elector reaches this fragment and holds no role in `identity.sittings` at
// all.
//
// The design previews below them are still invented fixtures.
//
// ── THE DOOR IS IN FRONT OF EVERYTHING, AND THAT IS A CHANGE ───────────────
// This shell used to render a sample ballot paper to anybody who opened the
// page. It now renders the shared <LoginScreen> until the worker says who you
// are. The previews still show invented fixtures and still call nothing — they
// are safe by their content, not by being behind the door — but putting them
// behind it is the honest arrangement: pass 3 wires the results sheet to
// GET /api/results/:ballotId, and a habit of reaching a results URL without
// signing in is exactly the habit that must not exist when that lands.
//
// ── STILL NO ROUTING DEPENDENCY ────────────────────────────────────────────
// One URL fragment, read here, as before. What is new is that the fragment is
// only consulted once there is a session; signed out, there is one screen and
// the fragment means nothing. That is three lines of platform API instead of a
// routing library, and it keeps the door impossible to route around.
//
// ── THE SERVER DECIDES THE READER, ALWAYS ──────────────────────────────────
// `identity` here is whatever `GET /api/auth/me` last returned. It is never
// assembled from a stored role, never inferred from the token, and never cached
// across a load. And it authorises nothing on its own: it decides what this
// shell DRAWS, while every restricted surface re-resolves what may be SERVED on
// the request that asks for it. A screen deciding it may render something is not
// the same act as a server deciding it may send it, and only the second one is a
// control.

import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader } from '@cca/ui';
import { BallotPaper } from './BallotPaper';
import { CastPage } from './cast/CastPage';
import { DecidesSignIn } from './auth/DecidesSignIn';
import { Landing } from './Landing';
import { ScrutineerPage } from './scrutineer/ScrutineerPage';
import { SetupPage } from './setup/SetupPage';
import { SAMPLE_PAPER } from './fixtures/sample-paper';
import { SAMPLE_RESULTS } from './fixtures/sample-results';
import { GENERAL_PORTAL_NAME, portalNameFor } from './portal-name';
import { RegisterPage } from './register/RegisterPage';
import { ResultPage } from './results/ResultPage';
import {
  api,
  clearSessionToken,
  sessionToken,
  DecidesApiError,
  SESSION_EXPIRED_EVENT,
  type Identity,
} from './api/client';

function currentFragment(): string {
  return window.location.hash.replace(/^#\/?/, '');
}

/**
 * What the shell knows about the person right now.
 *
 * `no-grant` is its own state rather than an error string, because it is a
 * DIFFERENT thing to be told than a failure: the session is fine, the person is
 * who they say they are, and the register simply does not seat them. Folding it
 * into `error` would produce "something went wrong" for a case where nothing
 * did.
 */
type Session =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; identity: Identity }
  | { status: 'no-grant' }
  | { status: 'error' };

export function App(): JSX.Element {
  const [session, setSession] = useState<Session>(() =>
    // No token means no request: asking /me without one is a guaranteed 401, and
    // a first paint that flashes the door is better than one that waits on a
    // round trip to show it.
    sessionToken() === null ? { status: 'signed-out' } : { status: 'checking' },
  );
  const [fragment, setFragment] = useState(currentFragment);
  const [signingOut, setSigningOut] = useState(false);

  const loadIdentity = useCallback(async (): Promise<void> => {
    setSession({ status: 'checking' });
    try {
      setSession({ status: 'signed-in', identity: await api.me() });
    } catch (err) {
      if (err instanceof DecidesApiError && err.status === 401) {
        // apiFetch already cleared the token and announced it.
        setSession({ status: 'signed-out' });
      } else if (err instanceof DecidesApiError && err.status === 403) {
        setSession({ status: 'no-grant' });
      } else {
        setSession({ status: 'error' });
      }
    }
  }, []);

  useEffect(() => {
    if (sessionToken() !== null) void loadIdentity();
  }, [loadIdentity]);

  useEffect(() => {
    const onHashChange = (): void => {
      setFragment(currentFragment());
    };
    const onSessionExpired = (): void => {
      setSession({ status: 'signed-out' });
    };
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, []);

  /** Leave a preview for the landing. Sets the hash AND the state: relying on
   *  the `hashchange` event alone would leave the screen stale if the fragment
   *  were already empty, and setting state alone would leave the URL lying. */
  const goToLanding = useCallback((): void => {
    window.location.hash = '';
    setFragment('');
  }, []);

  const handleSignOut = useCallback(async (): Promise<void> => {
    setSigningOut(true);
    try {
      await api.logout();
    } catch {
      // The local session ends either way. A network failure on the way out must
      // not strand somebody signed in on a shared machine — the worst case is a
      // server-side record that expires on its own.
    }
    clearSessionToken();
    setSigningOut(false);
    setSession({ status: 'signed-out' });
    window.location.hash = '';
  }, []);

  if (session.status === 'checking') {
    // A held session is being re-checked against the server. `role="status"` so
    // the wait is announced rather than being a silently blank page.
    return (
      <Chrome>
        <p role="status">Checking your session…</p>
      </Chrome>
    );
  }

  if (session.status === 'signed-out') {
    return <DecidesSignIn onSignedIn={() => void loadIdentity()} />;
  }

  if (session.status === 'no-grant') {
    return (
      <Chrome>
        <h2>Nothing here for you yet</h2>
        {/* THREE STANDINGS, NOT TWO, since the cast path: the Synod Admin and the
            Scrutineers hold ROLES in the grant register, and a member of Synod
            holds a place on the frozen ROLL. This sentence used to name only the
            first two, which would tell an elector whose roll standing had not
            been found that the portal was never meant for them. */}
        <p>
          The {GENERAL_PORTAL_NAME} is used by the Synod Admin and the Scrutineers to run a sitting,
          and by members of Synod to vote in one. Your address is signed in, but it holds neither
          role in any sitting and is not on the roll for one.
        </p>
        <p>
          If that is unexpected, contact{' '}
          <a href="mailto:registrar@confessinganglicans.nz">registrar@confessinganglicans.nz</a>.
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => void handleSignOut()}>
          Sign out
        </button>
      </Chrome>
    );
  }

  if (session.status === 'error') {
    return (
      <Chrome>
        <h2>The {GENERAL_PORTAL_NAME} is not reachable</h2>
        <p className="alert">
          The sign-in service did not answer. This is not a problem with your account. Try again in
          a moment.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => void loadIdentity()}>
          Try again
        </button>
      </Chrome>
    );
  }

  // ── Signed in ────────────────────────────────────────────────────────────
  // THE REGISTER IS THE FIRST FRAGMENT THAT CARRIES REAL DATA, and it is the
  // first that carries an ARGUMENT — `#register/<electionId>`. Everything before
  // it renders an invented fixture and calls nothing.
  //
  // THE FRAGMENT AUTHORISES NOTHING, and that is worth stating here because this
  // is the line a later reader will copy for the results sheet. Reaching this URL
  // gets you the screen; getting anything ON it takes an admin grant for that
  // sitting, re-resolved server-side on the request that asks. A person who types
  // somebody else's election id sees a refusal, not a register.
  if (fragment.startsWith('register/')) {
    const electionId = decodeURIComponent(fragment.slice('register/'.length));
    if (electionId !== '') {
      // `identity` is handed down for the header's name and for nothing else —
      // the register's own route re-resolves the role on every request it serves.
      return (
        <RegisterPage electionId={electionId} identity={session.identity} onBack={goToLanding} />
      );
    }
  }

  // THE OPERATOR SETUP HUB — the Synod Admin gets a sitting ready: build and freeze
  // the roll, and take each ballot from draft to open. Same shape and same rule as
  // the register above — the fragment gets you the screen and authorises nothing;
  // every act is re-gated server-side to an admin of this sitting. `identity` is
  // handed down for the header's name only.
  if (fragment.startsWith('setup/')) {
    const electionId = decodeURIComponent(fragment.slice('setup/'.length));
    if (electionId !== '') {
      return <SetupPage electionId={electionId} identity={session.identity} onBack={goToLanding} />;
    }
  }

  // THE CAST SCREEN — a member of Synod votes. Same shape as the register above
  // and the same rule: the fragment gets you the screen and authorises nothing.
  // Standing to hold a paper comes from the frozen ROLL, re-resolved server-side
  // on the request that issues it, so a member who types somebody else's ballot
  // id is refused a paper rather than shown one. `identity` is handed down for
  // the header's name and for the "you are voting as" line — the roll holds no
  // names, so the address the person signed in with is the honest answer.
  if (fragment.startsWith('ballot/')) {
    const ballotId = decodeURIComponent(fragment.slice('ballot/'.length));
    if (ballotId !== '') {
      return <CastPage ballotId={ballotId} identity={session.identity} onBack={goToLanding} />;
    }
  }

  // THE SCRUTINEER'S OPERATOR PANEL — close → seal → count → declare, one lawful
  // act at a time, and the results sheet rendered from the count it runs. Same
  // rule as the two fragments above: reaching this URL gets you the screen and
  // authorises nothing — every act is re-gated server-side to a scrutineer of this
  // sitting (SO 15.11). `identity` is handed down for the header's name only.
  if (fragment.startsWith('scrutineer/')) {
    const ballotId = decodeURIComponent(fragment.slice('scrutineer/'.length));
    if (ballotId !== '') {
      return (
        <ScrutineerPage ballotId={ballotId} identity={session.identity} onBack={goToLanding} />
      );
    }
  }

  const sheet = SAMPLE_RESULTS[fragment];
  if (sheet !== undefined) {
    return (
      <>
        <PreviewBar onBack={goToLanding} />
        <ResultPage view={sheet} />
      </>
    );
  }

  if (fragment === 'paper') {
    return (
      <Chrome>
        <PreviewNotice onBack={goToLanding} />
        <BallotPaper view={SAMPLE_PAPER} />
      </Chrome>
    );
  }

  return (
    <Landing
      identity={session.identity}
      onSignOut={() => void handleSignOut()}
      signingOut={signingOut}
    />
  );
}

/** The estate header plus this app's page frame — shared by every screen the
 *  shell draws itself, so none of them re-states the layout.
 *
 *  `portalNameFor(null)` — not an oversight. Chrome is drawn in exactly three
 *  states (checking, no-grant, error) and NONE of them has an identity: in two
 *  the server has not said who is asking, and in the third it has said nobody.
 *  So the general name, for the reasons in ./portal-name.ts. */
function Chrome({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="ballot-app min-dvh">
      <PortalHeader portalName={portalNameFor(null)} />
      <main className="ballot-main">
        <div className="page-narrow">{children}</div>
      </main>
    </div>
  );
}

/**
 * Preview furniture, and deliberately NOT part of the sheet. The results sheet
 * shows no controls of its own beyond its provenance disclosure — that is the
 * discrete-interface rule and it is load-bearing — so the way back belongs to a
 * bar above it that goes away when real data arrives.
 *
 * THE WAY BACK IS A BUTTON, NOT A LINK, and the distinction is not pedantry: an
 * `<a href="#">` is announced to a screen reader as a link and goes nowhere when
 * followed. This changes app state and updates the URL itself, so the control
 * that does it is a button (jsx-a11y/anchor-is-valid). The links INTO the
 * previews are real anchors to real fragments and stay anchors.
 */
function PreviewBar({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <p className="alert ballot-preview-notice">
      <strong>Sample results.</strong> Invented data; not connected to any sitting.{' '}
      <button type="button" className="btn btn-ghost" onClick={onBack}>
        Back to your sittings
      </button>
    </p>
  );
}

function PreviewNotice({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <p className="alert ballot-preview-notice">
      <strong>Sample paper.</strong> This is a preview of the ballot paper using invented data. No
      ballot is open, and nothing you do here is recorded.{' '}
      <button type="button" className="btn btn-ghost" onClick={onBack}>
        Back to your sittings
      </button>
    </p>
  );
}
